// client/src/pages/admin/ManageNotificationEmailTemplates.js
//
// Admin UI for notification email templates with per-user-type activation.
//
// MODEL (schema21):
//   Each user type (Free, Stardust, Fairydust, Business Wizard, ...) can
//   have at most ONE active notification email template. Activation lives
//   in the notification_email_template_user_types junction table's
//   isactive flag. Each (template × user type) combination is in one of
//   three states:
//
//     • Not Assigned — template invisible to that tier
//     • Assigned     — junction row exists, isactive = false
//                      (template is "in the pool" but not used)
//     • Active       — junction row exists, isactive = true
//                      (THIS is what users of that tier receive)
//
// PROMOTION RULE:
//   Activating a template for a tier automatically demotes whatever was
//   previously Active for that same tier back to Assigned. The server
//   handles this in admin.js → applyTemplateAssignments.
//
// REQUEST SHAPE:
//   POST/PUT body:
//     {
//       name, description, htmlcontent, textcontent,
//       unsubscribe_template_id, isactive,
//       user_type_assignments: [
//         { usertypeid: 1, isactive: true  },   // tier 1 active
//         { usertypeid: 2, isactive: false },   // tier 2 assigned only
//         // tier 3 omitted entirely => not assigned
//       ]
//     }

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api.js';
import Button from '../../components/ui/Button.js';
import Modal from '../../components/ui/Modal.js';

// Per-tier state values used by the form.
const TIER_STATE = {
  NOT_ASSIGNED: 'not_assigned',
  ASSIGNED: 'assigned',
  ACTIVE: 'active',
};

export default function ManageNotificationEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [unsubscribeTemplates, setUnsubscribeTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('html'); // 'html' or 'text'
  const [saving, setSaving] = useState(false);

  // Form state. tierStates is a map: usertypeid → 'not_assigned' | 'assigned' | 'active'
  const [form, setForm] = useState({
    name: '',
    description: '',
    htmlcontent: '',
    textcontent: '',
    unsubscribe_template_id: null,
    isactive: true,
    tierStates: {},
  });

  const load = async () => {
    try {
      const data = await api.get('/admin/notification-email-templates');
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Load notification email templates error:', err);
    }
  };

  const loadUserTypes = async () => {
    try {
      const data = await api.get('/admin/user-types');
      setUserTypes(data.userTypes || []);
    } catch (err) {
      console.error('Load user types error:', err);
    }
  };

  const loadUnsubscribeTemplates = async () => {
    try {
      const data = await api.get('/admin/unsubscribe-templates-active');
      setUnsubscribeTemplates(data.unsubscribeTemplates || []);
    } catch (err) {
      console.error('Load unsubscribe templates error:', err);
    }
  };

  useEffect(() => {
    load();
    loadUserTypes();
    loadUnsubscribeTemplates();
  }, []);

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  // Build the per-tier "what's active right now" map for the status banner.
  // Returns { [usertypeid]: { templateid, templateName } | null }
  // O(templates × tiers per template) — fine for the small datasets here.
  const activeByTier = useMemo(() => {
    const map = {};
    for (const ut of userTypes) {
      map[ut.usertypeid] = null;
    }
    for (const t of templates) {
      const assignments = t.user_type_assignments || [];
      for (const a of assignments) {
        if (a.isactive) {
          map[a.usertypeid] = { templateid: t.templateid, templateName: t.name };
        }
      }
    }
    return map;
  }, [templates, userTypes]);

  // For a given template, which user types is it currently active for?
  // Used to render "Active for: Free, Stardust" pills in the list view.
  const activeTiersForTemplate = (template) => {
    const assignments = template.user_type_assignments || [];
    return assignments
      .filter(a => a.isactive)
      .map(a => a.typename);
  };

  // For a given template, which user types is it assigned to (any state)?
  const assignedTiersForTemplate = (template) => {
    const assignments = template.user_type_assignments || [];
    return assignments.map(a => a.typename);
  };

  // Initialize form for a NEW template — every tier starts Not Assigned.
  const openNew = () => {
    setEditing(null);
    const initialStates = {};
    for (const ut of userTypes) {
      initialStates[ut.usertypeid] = TIER_STATE.NOT_ASSIGNED;
    }
    setForm({
      name: '',
      description: '',
      htmlcontent: '',
      textcontent: '',
      unsubscribe_template_id: null,
      isactive: true,
      tierStates: initialStates,
    });
    setActiveTab('html');
    setShowForm(true);
  };

  // Initialize form for EDIT — derive tier states from the template's
  // current assignments. Tiers without an assignment row are Not Assigned.
  const startEdit = (t) => {
    setEditing(t.templateid);

    const assignments = t.user_type_assignments || [];
    const assignmentsByTier = {};
    for (const a of assignments) {
      assignmentsByTier[a.usertypeid] = a.isactive ? TIER_STATE.ACTIVE : TIER_STATE.ASSIGNED;
    }

    const initialStates = {};
    for (const ut of userTypes) {
      initialStates[ut.usertypeid] = assignmentsByTier[ut.usertypeid] || TIER_STATE.NOT_ASSIGNED;
    }

    setForm({
      name: t.name,
      description: t.description || '',
      htmlcontent: t.htmlcontent || '',
      textcontent: t.textcontent || '',
      unsubscribe_template_id: t.unsubscribe_template_id || null,
      isactive: t.isactive !== false,
      tierStates: initialStates,
    });
    setActiveTab('html');
    setShowForm(true);
  };

  // Convert form.tierStates back to the API's user_type_assignments shape.
  // Tiers in NOT_ASSIGNED state are omitted (they have no junction row).
  const buildAssignmentsPayload = () => {
    const result = [];
    for (const [usertypeid, state] of Object.entries(form.tierStates)) {
      if (state === TIER_STATE.NOT_ASSIGNED) continue;
      result.push({
        usertypeid: parseInt(usertypeid, 10),
        isactive: state === TIER_STATE.ACTIVE,
      });
    }
    return result;
  };

  // Update one tier's state. If we're setting a tier to ACTIVE and that
  // tier already has a different template active, optimistically demote
  // the OTHER template in the form's tier states is a no-op (we're only
  // editing one template at a time). The server will demote the other
  // template's row in its DB when we save. We don't need to touch other
  // templates locally.
  const setTierState = (usertypeid, newState) => {
    setForm((f) => ({
      ...f,
      tierStates: { ...f.tierStates, [usertypeid]: newState },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        htmlcontent: form.htmlcontent,
        textcontent: form.textcontent,
        unsubscribe_template_id: form.unsubscribe_template_id,
        isactive: form.isactive,
        user_type_assignments: buildAssignmentsPayload(),
      };

      if (editing) {
        await api.put(`/admin/notification-email-templates/${editing}`, payload);
        showMsg('Template updated');
      } else {
        await api.post('/admin/notification-email-templates', payload);
        showMsg('Template created');
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      showMsg(err.data?.error || 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    const activeFor = activeTiersForTemplate(template);
    if (activeFor.length > 0) {
      showMsg(
        `This template is currently active for: ${activeFor.join(', ')}. ` +
        `Activate a different template for those tier(s) first, or set this one to "Assigned" before deleting.`,
        'error'
      );
      return;
    }

    if (!window.confirm('Delete this notification email template?')) return;
    try {
      await api.del(`/admin/notification-email-templates/${template.templateid}`);
      showMsg('Template deleted');
      load();
    } catch (err) {
      showMsg(err.data?.error || 'Failed to delete', 'error');
    }
  };

  const getUnsubscribeTemplateName = (id) => {
    if (!id) return null;
    const t = unsubscribeTemplates.find((u) => u.templateid === id);
    return t?.name || 'Unknown';
  };

  // Build the sample preview. Mirrors what the server emits so admins see
  // what users actually receive: "Sarah," salutation, no personal message,
  // no closing.
  const buildSamplePreview = (html, unsubscribeTemplateId) => {
    if (!html) return '<p style="padding:20px;color:#888;">No HTML content</p>';
    let preview = html;
    preview = preview.replace(/\{\{app_name\}\}/g, 'The Cherry Fairy Shop');
    preview = preview.replace(/\{\{sender_name\}\}/g, 'Harold Stong');
    preview = preview.replace(/\{\{card_url\}\}/g, '#');
    preview = preview.replace(/\{\{salutation_block\}\}/g,
      '<p style="font-size:16px;color:#333;margin:0 0 16px 0;text-align:left;">Sarah,</p>'
    );
    preview = preview.replace(/\{\{personal_message_block\}\}/g, '');
    preview = preview.replace(/\{\{closing_block\}\}/g, '');
    preview = preview.replace(/\{\{unsubscribe_url\}\}/g, '#');
    preview = preview.replace(/\{\{privacy_url\}\}/g, '#');

    if (unsubscribeTemplateId) {
      preview += `
        <table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Georgia,serif;">
          <tr>
            <td style="padding:24px 30px;text-align:center;background:#fafafa;border-top:1px solid #e0e0e0;">
              <p style="font-size:12px;color:#888;margin:0 0 8px 0;line-height:1.5;">
                You received this because Harold Stong sent you an eCard via The Cherry Fairy Shop.
              </p>
              <p style="font-size:12px;color:#888;margin:0;line-height:1.5;">
                <a href="#" style="color:#666;text-decoration:underline;">Unsubscribe from Harold Stong</a>
                &nbsp;&middot;&nbsp;
                <a href="#" style="color:#666;text-decoration:underline;">Unsubscribe from all eCards</a>
                &nbsp;&middot;&nbsp;
                <a href="#" style="color:#666;text-decoration:underline;">Privacy</a>
              </p>
            </td>
          </tr>
        </table>
      `;
    }

    return preview;
  };

  // Three-state segmented control rendered for each user type in the
  // edit form. Compact so it doesn't blow up the modal vertically.
  function TierStateToggle({ usertypeid, state, onChange, currentlyActiveTemplateName, isThisTemplate }) {
    const isActive = state === TIER_STATE.ACTIVE;
    const isAssigned = state === TIER_STATE.ASSIGNED;
    const isNotAssigned = state === TIER_STATE.NOT_ASSIGNED;

    // If this tier is currently active for a DIFFERENT template, show a
    // hint so the admin knows activating here will demote the other one.
    const willDemote =
      currentlyActiveTemplateName &&
      !isThisTemplate &&
      !isActive; // hint only when admin hasn't already chosen Active

    return (
      <div className="flex flex-col gap-1">
        <div className="inline-flex rounded-lg border border-royal-600 overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => onChange(TIER_STATE.NOT_ASSIGNED)}
            className={`px-3 py-1.5 transition-colors ${
              isNotAssigned
                ? 'bg-royal-600 text-white'
                : 'bg-royal-800/40 text-royal-200 hover:bg-royal-700/40'
            }`}
          >
            Not Assigned
          </button>
          <button
            type="button"
            onClick={() => onChange(TIER_STATE.ASSIGNED)}
            className={`px-3 py-1.5 transition-colors border-l border-royal-600 ${
              isAssigned
                ? 'bg-blue-500/40 text-white'
                : 'bg-royal-800/40 text-royal-200 hover:bg-royal-700/40'
            }`}
          >
            Assigned
          </button>
          <button
            type="button"
            onClick={() => onChange(TIER_STATE.ACTIVE)}
            className={`px-3 py-1.5 transition-colors border-l border-royal-600 ${
              isActive
                ? 'bg-green-500/40 text-white'
                : 'bg-royal-800/40 text-royal-200 hover:bg-royal-700/40'
            }`}
          >
            Active
          </button>
        </div>
        {willDemote && (
          <p className="text-amber-400 text-xs leading-tight">
            Currently active: <span className="font-medium">{currentlyActiveTemplateName}</span>.
            Activating here will replace it.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl text-gold">Email Templates</h1>
          <p className="text-royal-200 text-sm mt-1">
            Choose the active notification email per user tier. Each tier can have its own template.
          </p>
        </div>
        <Button onClick={openNew}>Add Template</Button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          message.type === 'error' ? 'bg-red-900/30 border border-red-700 text-red-400' : 'bg-green-900/30 border border-green-700 text-green-400'
        }`}>
          {message.text}
        </div>
      )}

      {/* Status banner — current active template per tier */}
      {userTypes.length > 0 && (
        <div className="mb-6 p-4 bg-royal-800/40 border border-royal-600 rounded-lg">
          <p className="text-royal-200 text-xs uppercase tracking-wide font-semibold mb-3">
            Active by Tier
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {userTypes.map((ut) => {
              const active = activeByTier[ut.usertypeid];
              return (
                <div
                  key={ut.usertypeid}
                  className={`p-3 rounded-lg border text-sm ${
                    active
                      ? 'bg-green-900/15 border-green-700/40'
                      : 'bg-amber-900/15 border-amber-700/40'
                  }`}
                >
                  <p className="text-white font-semibold text-sm">{ut.typename}</p>
                  {active ? (
                    <p className="text-green-400 text-xs mt-0.5 truncate" title={active.templateName}>
                      ● {active.templateName}
                    </p>
                  ) : (
                    <p className="text-amber-400 text-xs mt-0.5">⚠ None set — uses fallback email</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Templates list */}
      <div className="space-y-3">
        {templates.map((t) => {
          const activeFor = activeTiersForTemplate(t);
          const assignedTo = assignedTiersForTemplate(t);
          const isActiveSomewhere = activeFor.length > 0;

          return (
            <div
              key={t.templateid}
              className={`bg-royal-800/40 border rounded-lg p-4 transition-colors ${
                isActiveSomewhere ? 'border-green-500/40 ring-1 ring-green-500/10' : 'border-royal-600'
              }`}
            >
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white font-semibold">{t.name}</p>
                    {!t.isactive && (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full font-medium">
                        Disabled
                      </span>
                    )}
                    {t.unsubscribe_template_id && (
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                        Footer: {getUnsubscribeTemplateName(t.unsubscribe_template_id)}
                      </span>
                    )}
                    {t.textcontent && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                        Plain Text ✓
                      </span>
                    )}
                  </div>

                  {t.description && (
                    <p className="text-royal-200 text-sm mt-0.5">{t.description}</p>
                  )}

                  {/* Active for / Assigned to badges */}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {activeFor.length > 0 ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                        Active for: {activeFor.join(', ')}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-royal-700/40 text-royal-300 text-xs rounded-full">
                        Not active for any tier
                      </span>
                    )}
                    {assignedTo.length > activeFor.length && (
                      <span className="px-2 py-0.5 bg-blue-500/15 text-blue-300 text-xs rounded-full">
                        Assigned to: {assignedTo.filter(n => !activeFor.includes(n)).join(', ')}
                      </span>
                    )}
                  </div>

                  <p className="text-royal-200 text-xs mt-1.5">
                    Updated {new Date(t.updatedat || t.createdat).toLocaleDateString()}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 flex-shrink-0">
                  <button
                    className="text-blue-400 text-sm hover:text-blue-300 transition-colors"
                    onClick={() => setShowPreview(t)}
                  >
                    Preview
                  </button>
                  <button className="text-gold text-sm hover:text-gold-light transition-colors" onClick={() => startEdit(t)}>
                    Edit
                  </button>
                  <button
                    className="text-red-400 text-sm hover:text-red-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={() => handleDelete(t)}
                    disabled={isActiveSomewhere}
                    title={isActiveSomewhere
                      ? `Active for ${activeFor.join(', ')} — activate a replacement first`
                      : 'Delete this template'}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {templates.length === 0 && (
          <div className="text-center py-12">
            <p className="text-royal-200 text-base mb-3">No notification email templates yet.</p>
            <p className="text-royal-200 text-sm mb-4">Create one and activate it for the user tiers that should receive it.</p>
            <Button onClick={openNew}>Create First Template</Button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Email Template' : 'New Email Template'} maxWidth="max-w-4xl">
        <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto pr-2 space-y-4">
          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Template Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="e.g. Free Plan Standard, Business Wizard Premium"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description for admin reference"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>

          {/* HTML / Plain Text Tabs */}
          <div>
            <div className="flex items-center gap-1 mb-2">
              <button
                type="button"
                onClick={() => setActiveTab('html')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === 'html'
                    ? 'bg-royal-700 text-white border-b-2 border-gold'
                    : 'bg-royal-800/30 text-royal-300 hover:text-white'
                }`}
              >
                HTML Content *
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('text')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                  activeTab === 'text'
                    ? 'bg-royal-700 text-white border-b-2 border-gold'
                    : 'bg-royal-800/30 text-royal-300 hover:text-white'
                }`}
              >
                Plain Text Version
                {form.textcontent && <span className="text-green-400 text-xs">✓</span>}
              </button>
            </div>

            {activeTab === 'html' ? (
              <textarea
                value={form.htmlcontent}
                onChange={(e) => setForm({ ...form, htmlcontent: e.target.value })}
                required
                rows={14}
                placeholder="Paste your complete HTML email template here..."
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors font-mono resize-y"
              />
            ) : (
              <div>
                <textarea
                  value={form.textcontent}
                  onChange={(e) => setForm({ ...form, textcontent: e.target.value })}
                  rows={14}
                  placeholder={`Plain text version of the email (recommended for deliverability).

Example:
{{salutation}}

{{sender_name}} has sent you an eCard from {{app_name}}.

View your card: {{card_url}}

---
Sent with love via {{app_name}}`}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-300 focus:outline-none focus:border-gold/50 transition-colors font-mono resize-y"
                />
                <p className="text-royal-300 text-xs mt-2">
                  💡 <strong className="text-royal-200">Why plain text?</strong> Email clients send both versions together.
                  Recipients see HTML, but plain text helps with spam filters and accessibility.
                  If left empty, a basic fallback will be auto-generated.
                </p>
              </div>
            )}
          </div>

          {/* Placeholder reference */}
          <div className="p-3 bg-royal-900/50 rounded-lg">
            <p className="text-royal-200 text-xs font-semibold mb-2">Available Placeholders:</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gold font-mono">{'{{app_name}}'}</span><span className="text-royal-200">Site name (from settings)</span>
              <span className="text-gold font-mono">{'{{sender_name}}'}</span><span className="text-royal-200">Sender's display name (or override)</span>
              <span className="text-gold font-mono">{'{{card_url}}'}</span><span className="text-royal-200">Link to view the eCard</span>
              <span className="text-gold font-mono">{'{{salutation_block}}'}</span><span className="text-royal-200">Always renders as "{'{firstname}'},"</span>
              <span className="text-gold font-mono">{'{{personal_message_block}}'}</span><span className="text-royal-200">Always empty (kept for back-compat)</span>
              <span className="text-gold font-mono">{'{{closing_block}}'}</span><span className="text-royal-200">Always empty (kept for back-compat)</span>
              <span className="text-gold font-mono">{'{{salutation}}'}</span><span className="text-royal-200">Plain text salutation</span>
            </div>
            <p className="text-royal-300 text-xs mt-2 leading-relaxed">
              <strong className="text-royal-200">Note:</strong> The send flow no longer collects a salutation, closing, or
              personal message from the user. <code className="text-gold">{'{{salutation_block}}'}</code> always renders as
              just the recipient's first name with a comma (e.g. "Sarah,"). The message and closing placeholders are
              left in place for backward compatibility but always render as empty strings.
            </p>
          </div>

          {/* Unsubscribe Template Dropdown */}
          <div className="p-4 bg-royal-900/50 border border-royal-700 rounded-lg">
            <label className="block text-sm text-royal-200 font-semibold mb-2">Unsubscribe Footer Template</label>
            <select
              value={form.unsubscribe_template_id || ''}
              onChange={(e) => setForm({ ...form, unsubscribe_template_id: e.target.value ? parseInt(e.target.value) : null })}
              className="w-full px-3 py-2.5 bg-royal-800 border border-royal-600 rounded-lg text-sm text-white focus:outline-none focus:border-gold/50 transition-colors"
            >
              <option value="">None (no unsubscribe footer)</option>
              {unsubscribeTemplates.map((ut) => (
                <option key={ut.templateid} value={ut.templateid}>
                  {ut.name} {ut.isdefault ? '(Default)' : ''}
                </option>
              ))}
            </select>
            <p className="text-royal-300 text-xs mt-2">
              The selected footer will be appended to every email using this template, with placeholders replaced.
              {unsubscribeTemplates.length === 0 && (
                <span className="text-amber-400">
                  {' '}No unsubscribe templates found. <Link to="/admin/unsubscribe-templates" className="text-gold hover:underline">Create one →</Link>
                </span>
              )}
            </p>
          </div>

          {/* Per-user-type assignment with three-state toggle */}
          <div className="p-4 bg-royal-900/50 border border-royal-700 rounded-lg">
            <p className="text-sm text-royal-200 font-semibold mb-1">User Tier Assignment</p>
            <p className="text-royal-300 text-xs mb-4 leading-relaxed">
              For each tier, choose <strong className="text-royal-200">Not Assigned</strong> (template invisible),
              {' '}<strong className="text-royal-200">Assigned</strong> (in the pool, not in use), or
              {' '}<strong className="text-royal-200">Active</strong> (this is the email senders on that tier
              will trigger). Activating here automatically deactivates whatever was previously active for that tier.
            </p>

            {userTypes.length === 0 ? (
              <p className="text-royal-300 text-xs italic">Loading user types...</p>
            ) : (
              <div className="space-y-2">
                {userTypes.map((ut) => {
                  const state = form.tierStates[ut.usertypeid] || TIER_STATE.NOT_ASSIGNED;
                  const currentActive = activeByTier[ut.usertypeid];
                  const isThisTemplate = currentActive && currentActive.templateid === editing;

                  return (
                    <div
                      key={ut.usertypeid}
                      className="flex items-center justify-between gap-3 p-2 bg-royal-800/40 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{ut.typename}</p>
                        {ut.tagline && (
                          <p className="text-royal-300 text-xs truncate">{ut.tagline}</p>
                        )}
                      </div>
                      <TierStateToggle
                        usertypeid={ut.usertypeid}
                        state={state}
                        onChange={(newState) => setTierState(ut.usertypeid, newState)}
                        currentlyActiveTemplateName={currentActive?.templateName}
                        isThisTemplate={isThisTemplate}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Template-level enabled (kill switch) */}
          <div className="p-4 bg-royal-900/50 border border-royal-700 rounded-lg">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isactive}
                onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
                className="mt-0.5 w-5 h-5 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/50"
              />
              <div>
                <span className="text-white text-sm font-medium">Template is enabled</span>
                <p className="text-royal-200 text-xs mt-0.5">
                  Master kill switch. If disabled, this template is never used regardless of per-tier
                  activation. Use to retire a template without removing its tier assignments.
                </p>
              </div>
            </label>
          </div>

          {/* Live preview */}
          {form.htmlcontent && (
            <div>
              <label className="block text-sm text-royal-200 mb-1.5">Preview (with sample data)</label>
              <div className="rounded-lg border border-royal-600 overflow-hidden">
                <iframe
                  srcDoc={buildSamplePreview(form.htmlcontent, form.unsubscribe_template_id)}
                  title="Email Template Preview"
                  className="w-full border-0 bg-white"
                  style={{ minHeight: '450px', display: 'block' }}
                  sandbox="allow-same-origin"
                />
              </div>
              <p className="text-royal-300 text-xs mt-2 italic">
                Preview reflects what users actually receive: a transactional email with just "Sarah," as the
                salutation. The personal message and closing blocks render empty.
              </p>
            </div>
          )}

          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : (editing ? 'Update Template' : 'Create Template')}
          </Button>
        </form>
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={!!showPreview} onClose={() => setShowPreview(null)} title={`Preview: ${showPreview?.name || ''}`} maxWidth="max-w-3xl">
        {showPreview && (
          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <p className="text-royal-200 text-xs italic flex-1">
                Showing with sample data. Actual emails will use real sender and recipient names — the rest is fixed.
              </p>
              {activeTiersForTemplate(showPreview).length > 0 && (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex-shrink-0 font-medium">
                  Active for: {activeTiersForTemplate(showPreview).join(', ')}
                </span>
              )}
              {showPreview.unsubscribe_template_id && (
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full flex-shrink-0">
                  + {getUnsubscribeTemplateName(showPreview.unsubscribe_template_id)} footer
                </span>
              )}
              {showPreview.textcontent && (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex-shrink-0">
                  Plain text included
                </span>
              )}
            </div>
            <div className="rounded-lg border border-royal-600 overflow-hidden">
              <iframe
                srcDoc={buildSamplePreview(showPreview.htmlcontent, showPreview.unsubscribe_template_id)}
                title="Email Template Preview"
                className="w-full border-0 bg-white"
                style={{ minHeight: '500px', display: 'block' }}
                sandbox="allow-same-origin"
              />
            </div>
            {showPreview.textcontent && (
              <div className="mt-4">
                <label className="block text-sm text-royal-200 mb-1.5">Plain Text Version</label>
                <pre className="p-4 bg-royal-900 border border-royal-700 rounded-lg text-sm text-royal-300 font-mono whitespace-pre-wrap overflow-x-auto max-h-48">
                  {showPreview.textcontent}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}